import { UserFriendlyError } from '@affine/error';
import {
  getPublicUserByIdQuery,
  getWorkspaceInfoQuery,
  gqlFetcherFactory,
  type GraphQLQuery,
  type QueryOptions,
  type QueryResponse,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { fromPromise, Service } from '@toeverything/infra';
import type { Observable } from 'rxjs';

import { AuthService } from './auth';
import type { FetchService } from './fetch';
import { defaultRequestCoordinator } from './request-coordinator';

let graphQLServiceSequence = 0;
const COMPLETED_QUERY_REUSE_MS = new Map<string, number>([
  [getPublicUserByIdQuery.id, 500],
  // Workspace boot has multiple independent consumers (engine, permissions
  // and recovery). The later consumers can mount more than 500 ms after the
  // first response even though they need the same immutable boot snapshot.
  // Mutations still clear this cache immediately below.
  [getWorkspaceInfoQuery.id, 2_000],
  // The settings bridge can receive a pending local signal while its initial
  // remote pull is still running. Reuse that completed pull unless an
  // intervening settings push (a mutation) invalidates it.
  ['uiSettingsVaultEntriesQuery', 2_000],
]);

interface RecentQueryResult {
  expiresAt: number;
  value: unknown;
}

export class GraphQLService extends Service {
  constructor(private readonly fetcher: FetchService) {
    super();
  }

  private readonly coordinatorScope = `graphql-service-${++graphQLServiceSequence}`;
  private readonly rawGql = gqlFetcherFactory('/graphql', this.fetcher.fetch);
  private readonly recentQueryResults = new Map<string, RecentQueryResult>();

  rxGql = <Query extends GraphQLQuery>(
    options: QueryOptions<Query>
  ): Observable<QueryResponse<Query>> => {
    return fromPromise(signal => {
      return this.gql({
        ...options,
        context: {
          signal,
          ...options.context,
        },
      } as any);
    });
  };

  gql = async <Query extends GraphQLQuery>(
    options: QueryOptions<Query>
  ): Promise<QueryResponse<Query>> => {
    try {
      if (!/^\s*query\b/.test(options.query.query)) {
        this.recentQueryResults.clear();
        return await this.rawGql(options);
      }

      const variables =
        (
          options as unknown as {
            variables?: Record<string, unknown>;
          }
        ).variables ?? {};
      const workspaceId =
        typeof variables.workspaceId === 'string'
          ? variables.workspaceId
          : '__server__';
      const externalSignal = options.context?.signal ?? undefined;
      if (externalSignal?.aborted) {
        throw externalSignal.reason;
      }
      const coordinatorKey = `${this.coordinatorScope}:${options.query.id}:${JSON.stringify(
        variables
      )}`;
      const reuseMs = COMPLETED_QUERY_REUSE_MS.get(options.query.id);
      const recent = reuseMs
        ? this.recentQueryResults.get(coordinatorKey)
        : undefined;
      if (recent && recent.expiresAt >= Date.now()) {
        return recent.value as QueryResponse<Query>;
      }
      if (recent) {
        this.recentQueryResults.delete(coordinatorKey);
      }

      const result = await defaultRequestCoordinator.execute<
        QueryResponse<Query>
      >({
        workspaceId,
        category: 'workspace-api',
        key: coordinatorKey,
        priority: 'visible',
        mode: 'background',
        signal: externalSignal,
        run: signal =>
          this.rawGql({
            ...options,
            context: {
              ...options.context,
              signal,
            },
          } as unknown as QueryOptions<Query>),
      });
      if (reuseMs) {
        this.recentQueryResults.set(coordinatorKey, {
          expiresAt: Date.now() + reuseMs,
          value: result,
        });
      }
      return result;
    } catch (anyError) {
      const error = UserFriendlyError.fromAny(anyError);

      if (error.isStatus(401)) {
        this.framework.get(AuthService).session.revalidate();
      }

      if (isRateLimitError(error)) {
        throw new UserFriendlyError({
          status: 429,
          code: error.code || 'RATE_LIMIT_EXCEEDED',
          type: error.type || error.code || 'RATE_LIMIT_EXCEEDED',
          name: 'TOO_MANY_REQUEST',
          message: I18n['com.streamlain.error.too-many-requests.message'](),
          data: error.data,
          stacktrace: error.stacktrace,
        });
      }

      throw error;
    }
  };
}

function isRateLimitError(error: UserFriendlyError): boolean {
  return (
    error.isStatus(429) ||
    error.code === 'RATE_LIMIT_EXCEEDED' ||
    error.code === 'TOO_MANY_REQUEST' ||
    error.name === 'TOO_MANY_REQUEST'
  );
}
